import { Banner, FormContainer, FormStep, FormStepper, getErrorMessage, useIfUser } from '@if/mui'
import { useGenerateTestLoan, usePreviewTestLoan } from '@src/api/dev-tools/test-loan-api'
import type { TestLoanPreviewResult, TestLoanRequest } from '@src/types/test-loan'
import { useState } from 'react'
import { ValidationError } from '@if/core/yup'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import LoanParametersStep from './LoanParametersStep'
import PastPaymentsStep from './PastPaymentsStep'
import { TestLoanDraftType, TestLoanSchema } from './test-loan-schema'

const STEP1_FIELDS = [
  'financingProgramId',
  'firstName',
  'lastName',
  'fundedProducts',
  'interestRate',
  'frequency',
  'term',
  'stateIso',
  'activationPlannedOn',
  'firstPaymentExpectedOn',
] as const

// Steps run `trigger` before this, so the draft is complete: the schema cast yields the request shape.
const toRequest = (draft: TestLoanDraftType): TestLoanRequest => TestLoanSchema.validateSync(draft) as TestLoanRequest

// Two-step dev tool: parameters → preview of the past payments (toggle failures) → generate.
const LoanCreateWizard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useIfUser()
  const preview = usePreviewTestLoan()
  const generate = useGenerateTestLoan()
  const [previewResult, setPreviewResult] = useState<TestLoanPreviewResult | null>(null)
  // A schema violation `trigger` did not surface (should not happen: every rule is field-level).
  const [schemaError, setSchemaError] = useState<string | null>(null)

  return (
    <FormContainer
      mode="create"
      hideActions
      useFormSetup={() => ({
        schema: TestLoanSchema,
        defaultValues: { ...TestLoanSchema.getDefault(), firstName: user.firstName, lastName: user.lastName },
      })}
    >
      {({ control, trigger, getValues, setValue }) => (
        <FormStepper
          trigger={trigger}
          labels={{ submit: t('loan.createWizard.generate') }}
          onComplete={async () => {
            const created = await generate.mutateAsync(toRequest(getValues()))
            navigate(`/${created.financingCompanyId}/loans/${created.loanId}`)
          }}
        >
          <FormStep
            label={t('loan.createWizard.stepParameters')}
            fields={[...STEP1_FIELDS]}
            onNext={async () => {
              // Failures chosen for a previous preview no longer match the new schedule.
              setValue('failedPayments', [])
              setSchemaError(null)
              try {
                setPreviewResult(await preview.mutateAsync(toRequest(getValues())))
                return true
              } catch (error) {
                // The API error is shown through `preview.isError`; only a schema throw needs its own banner.
                if (error instanceof ValidationError) setSchemaError(error.message)
                return false
              }
            }}
          >
            {schemaError && <Banner severity="error">{schemaError}</Banner>}
            {preview.isError && <Banner severity="error">{getErrorMessage(preview.error)}</Banner>}
            <LoanParametersStep control={control} setValue={setValue} />
          </FormStep>
          <FormStep label={t('loan.createWizard.stepPayments')} fields={['failedPayments']}>
            {generate.isError && <Banner severity="error">{getErrorMessage(generate.error)}</Banner>}
            <PastPaymentsStep control={control} preview={previewResult} />
          </FormStep>
        </FormStepper>
      )}
    </FormContainer>
  )
}

export default LoanCreateWizard
